import { useState } from 'react'
import { Text, Stack, Tabs } from '@mantine/core'
import { useAuthStore, useCan } from '@/store/auth'
import { Callout } from '@/components/ui'
import { DashboardHeader } from '@/components/DashboardHeader'
import { InfrastructureTab } from './InfrastructureTab'
import { BusinessTab } from './BusinessTab'
import { ApplicationTab } from './ApplicationTab'
import { JiraTab } from './JiraTab'
import { WhatsApiTenantTab } from './WhatsApiTenantTab'
import { useDashboardData } from '@/hooks/useDashboardData'

/**
 * Dev Dashboard Component (TASK-1223, TASK-1224, TASK-1226)
 */
export function DevDashboard() {
  const { user } = useAuthStore()
  const can = useCan()
  const [activeTab, setActiveTab] = useState<string | null>('infrastructure')

  const { tenant, tenantId, preferences } = useDashboardData('dev')

  const tid = tenantId ?? null
  const canViewInfra = can('recursos', 'read', tid)
  const canViewBusiness = can('chamados', 'read', tid) || can('finops', 'read', tid)
  const canViewApp = can('dashboard', 'read', tid)
  const canViewJira = can('jira', 'read', tid)
  const canObserveWhatsApi = tenantId != null && (
    can('whatsapi', 'observe', tenantId) || can('whatsapi', 'read', tenantId)
  )

  const hiddenCards = preferences.data?.hidden_cards ?? []
  const showInfra = !hiddenCards.includes('infrastructure') && canViewInfra
  const showBusiness = !hiddenCards.includes('business') && canViewBusiness
  const showApp = !hiddenCards.includes('application') && canViewApp
  const showJira = canViewJira
  const showWhatsApi = canObserveWhatsApi

  const visibleTabs: string[] = []
  if (showInfra) visibleTabs.push('infrastructure')
  if (showBusiness) visibleTabs.push('business')
  if (showApp) visibleTabs.push('application')
  if (showJira) visibleTabs.push('jira')
  if (showWhatsApi) visibleTabs.push('whatsapi')

  // If no tabs are visible, show warning
  if (visibleTabs.length === 0) {
    return (
      <Stack gap="xl">
        <DashboardHeader title="Dashboard Dev" />
        <Callout variant="warning" title="Sem permissões">
          <Text size="sm">
            Você não possui permissões para visualizar nenhuma aba do dashboard.
          </Text>
        </Callout>
      </Stack>
    )
  }

  const initialTab = activeTab && visibleTabs.includes(activeTab) ? activeTab : visibleTabs[0]

  return (
    <Stack gap="xl">
      <Tabs value={initialTab} onChange={setActiveTab}>
        <Tabs.List>
          {showInfra && <Tabs.Tab value="infrastructure">Infraestrutura</Tabs.Tab>}
          {showBusiness && <Tabs.Tab value="business">Negócio</Tabs.Tab>}
          {showApp && <Tabs.Tab value="application">Aplicação</Tabs.Tab>}
          {showJira && <Tabs.Tab value="jira">Jira</Tabs.Tab>}
          {showWhatsApi && <Tabs.Tab value="whatsapi">WhatsApi</Tabs.Tab>}
        </Tabs.List>

        {showInfra && (
          <Tabs.Panel value="infrastructure" pt="xl">
            <InfrastructureTab />
          </Tabs.Panel>
        )}

        {showBusiness && (
          <Tabs.Panel value="business" pt="xl">
            <BusinessTab />
          </Tabs.Panel>
        )}

        {showApp && (
          <Tabs.Panel value="application" pt="xl">
            <ApplicationTab />
          </Tabs.Panel>
        )}

        {showJira && <Tabs.Panel value="jira" pt="xl"><JiraTab /></Tabs.Panel>}
        {showWhatsApi && (
          <Tabs.Panel value="whatsapi" pt="xl">
            <WhatsApiTenantTab tenantId={tenantId as number} />
          </Tabs.Panel>
        )}
      </Tabs>

      {!tenant && (
        <Callout variant="warning" title="Sem tenant associado">
          <Text size="sm">
            Peça ao administrador para associar um tenant ao seu usuário em{' '}
            <strong>Administração — Usuários</strong>.
          </Text>
        </Callout>
      )}
    </Stack>
  )
}
